// Browser-compatible cache service using localStorage and memory
class BrowserCacheService {
  constructor() {
    this.memoryCache = new Map();
    this.defaultTTL = 300; // 5 minutes default
  }

  // Generate cache key with namespace
  getCacheKey(namespace, key) {
    return `cache:${namespace}:${key}`;
  }

  // Get from cache with fallback to localStorage
  async get(namespace, key) {
    const cacheKey = this.getCacheKey(namespace, key);

    try {
      // Check memory cache first
      const memData = this.memoryCache.get(cacheKey);
      if (memData && memData.expiry > Date.now()) {
        return memData.value;
      }

      // Check localStorage
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.expiry > Date.now()) {
          // Restore to memory cache
          this.memoryCache.set(cacheKey, parsed);
          return parsed.value;
        }
        // Clean up expired item
        localStorage.removeItem(cacheKey);
      }

      return null;
    } catch (error) {
      console.error('Cache get error:', error);
      return null;
    }
  }

  // Set cache with TTL
  async set(namespace, key, value, ttl = this.defaultTTL) {
    const cacheKey = this.getCacheKey(namespace, key);
    const cacheData = {
      value,
      expiry: Date.now() + ttl * 1000,
    };

    try {
      // Store in memory
      this.memoryCache.set(cacheKey, cacheData);

      // Limit memory cache size
      if (this.memoryCache.size > 100) {
        const firstKey = this.memoryCache.keys().next().value;
        this.memoryCache.delete(firstKey);
      }

      // Try to store in localStorage
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));

        // Clean up old localStorage items if space is running low
        if (localStorage.length > 50) {
          this.cleanupLocalStorage();
        }
      } catch (e) {
        // localStorage might be full or disabled
        console.warn('localStorage unavailable:', e);
      }

      return true;
    } catch (error) {
      console.error('Cache set error:', error);
      return false;
    }
  }

  // Delete from cache
  async del(namespace, key) {
    const cacheKey = this.getCacheKey(namespace, key);

    try {
      this.memoryCache.delete(cacheKey);
      localStorage.removeItem(cacheKey);
      return true;
    } catch (error) {
      console.error('Cache delete error:', error);
      return false;
    }
  }

  // Clear entire namespace
  async clearNamespace(namespace) {
    const prefix = `cache:${namespace}:`;

    try {
      // Clear from memory cache
      for (const key of this.memoryCache.keys()) {
        if (key.startsWith(prefix)) {
          this.memoryCache.delete(key);
        }
      }

      // Clear from localStorage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));

      return true;
    } catch (error) {
      console.error('Cache clear namespace error:', error);
      return false;
    }
  }

  // Clean up expired localStorage items
  cleanupLocalStorage() {
    const now = Date.now();
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('cache:')) {
        try {
          const stored = localStorage.getItem(key);
          const parsed = JSON.parse(stored);
          if (parsed.expiry < now) {
            keysToRemove.push(key);
          }
        } catch (e) {
          // Invalid item, remove it
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  // Cache wrapper for async functions
  async cacheable(namespace, key, fn, ttl = this.defaultTTL) {
    // Try to get from cache first
    const cached = await this.get(namespace, key);
    if (cached !== null) {
      return cached;
    }

    // Execute function and cache result
    try {
      const result = await fn();
      await this.set(namespace, key, result, ttl);
      return result;
    } catch (error) {
      console.error('Cacheable function error:', error);
      throw error;
    }
  }

  // Batch get operation
  async mget(namespace, keys) {
    const results = {};
    const missingKeys = [];

    for (const key of keys) {
      const cached = await this.get(namespace, key);
      if (cached !== null) {
        results[key] = cached;
      } else {
        missingKeys.push(key);
      }
    }

    return { results, missingKeys };
  }

  // Get cache statistics
  getStats() {
    let localStorageCount = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('cache:')) {
        localStorageCount++;
      }
    }

    return {
      memoryCacheSize: this.memoryCache.size,
      localStorageCount,
      memoryCacheKeys: Array.from(this.memoryCache.keys()),
    };
  }

  // Cleanup method
  async cleanup() {
    this.memoryCache.clear();
    // Don't clear all localStorage, just cache items
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('cache:')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}

// Export singleton instance
const browserCacheService = new BrowserCacheService();
export default browserCacheService;
